/* eslint-disable @typescript-eslint/no-misused-promises */
import * as React from "react";
import DialogContent from "@mui/material/DialogContent";
import { IDiversionReason, IFlight, IGroundEvent, ICreateGTBData } from "@vmosolutions/oc-interfaces";
import { useState, useEffect, useMemo } from "react";
import moment from "moment-timezone";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { DialogActions, useTheme } from "@mui/material";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { ModalTitle, ModalButton, ModalErrorText } from "../../DialogModal/DialogModalComponents";
import { EventModalTypes } from "../DialogModalWrapper";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { updateAirportOptions } from "src/utils/airportHelper";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
// form components
import { DateTimeField } from "src/components/FormikComponents/DateTimeField";
import { SelectField } from "src/components/FormikComponents/SelectField";
import { TextField } from "src/components/FormikComponents/TextField";
import { debounce } from "lodash";
import { DialogModalStyles } from "../DialogModalWrapper";

/**
 * Modal appears only when 1 flight is slected and the ground turnback button is selected
 * gathers user info from via modal and dispatches an API call to update flight and create a ground event
 */

interface IGroundTurnBackModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
  timeFromReturntoGateToActualDeparture?: number;
  timeInputLabel1?: string;
  timeInputLabel2?: string;
}

export const GroundTurnBackModal = (props: IGroundTurnBackModalProps): JSX.Element => {
  const {
    isOpen,
    handleClose,
    setActiveEventModal,
    timeFromReturntoGateToActualDeparture,
    timeInputLabel1,
    timeInputLabel2,
  } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const customerStructure = useSelector((state: IRootState) => state.structures.customerStructure);
  const { flightTypes, aircraft, diversionReasons } = customerStructure;
  const airportOptions = useSelector((state: IRootState) => state.structures.airportOptions);

  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );

  const selectedFlights: IFlight[] = React.useMemo(() => {
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [selectedEvents]);

  const timeLeft: number = selectedFlights[0]?.actualOut?.valueOf();
  const timeReturn: number = selectedFlights[0]?.actualOut?.valueOf();

  const [groundTurnBackTime, setGroundTurnBackTime] = useState<{
    timeLeft: number;
    timeReturn: number;
  }>({ timeLeft: timeLeft, timeReturn: timeReturn });
  const [groundTurnBackReason, setGroundTurnBackReason] = useState<any>(null);
  const [groundTurnBackComment, setGroundTurnBackComment] = useState<any>(null);

  //error checking
  const [timeError, setTimeError] = useState<string>(null);
  const [textError, setTextError] = useState<string>(null);
  const [reasonError, setReasonError] = useState<any>(null);

  const [isMakingAPICall, setIsMakingAPICall] = useState<any>(null);

  const eventType = flightTypes.filter((option) => option.isGroundTurnBack);
  const selectedAircraft = aircraft.filter((option) => option._id === selectedFlights[0]?.aircraft._id);

  //get diversion reasons
  const diversionTitles = diversionReasons
    .map((opt: IDiversionReason) => {
      return {
        value: opt._id,
        label: opt.title,
      };
    })
    .sort((a, b) => {
      return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
    }); // sort options alphabetically.

  //check that there is selected flight with an orgin airport
  const airportIdent =
    selectedFlights.length > 0 && selectedFlights[0].hasOwnProperty("origin") ? selectedFlights[0].origin : null;

  useEffect(() => {
    if (airportIdent) {
      updateAirportOptions(airportIdent, airportOptions, dispatch);
    }
  }, [airportIdent, airportOptions, dispatch]);
  //currently set to increment the flight departure time by 5 minutes from the return to gate time
  const increment: number = useMemo(
    () => (timeFromReturntoGateToActualDeparture ? timeFromReturntoGateToActualDeparture : 5 * 60000),
    [timeFromReturntoGateToActualDeparture]
  );

  //set labels & error strings so they can be configurable
  const timeInputLabel: string[] = [
    timeInputLabel1 ? timeInputLabel1 : "Out Time",
    timeInputLabel2 ? timeInputLabel2 : "Return to Gate",
  ];
  const timeErrorString = `${timeInputLabel[1]} must occur after ${timeInputLabel[0]} `;

  useEffect(() => {
    if (groundTurnBackTime.timeLeft > groundTurnBackTime.timeReturn) {
      setTimeError(timeErrorString);
    } else {
      setTimeError(null);
    }
  }, [groundTurnBackTime, timeErrorString]);

  //check to make sure you aren't working with an empty array
  if (selectedEvents.length <= 0) {
    // Debug.log("selectedEvents array is empty")
    return null;
  }

  //////////////
  // HANDLERS //
  //////////////

  const arrayIsNotNull = (): boolean => {
    return selectedEvents.length > 0 ? true : false;
  };

  const handleMinDateChange = (event: any): void => {
    const timeRange = { ...groundTurnBackTime };
    timeRange.timeLeft = event;
    setGroundTurnBackTime(timeRange);
  };

  const handleMaxDateChange = (event: any): void => {
    const timeRange = { ...groundTurnBackTime };
    timeRange.timeReturn = event;
    setGroundTurnBackTime(timeRange);
  };

  const nativeFormHandler = (event: any): void => {
    setGroundTurnBackReason(event);
    setReasonError(null);
  };

  const textFieldHandler = (event: any): void => {
    setGroundTurnBackComment(event.target.value);
    setTextError(null);
  };

  const handleCancel = (): void => {
    //quit modal
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  const checkForErrors = (): boolean => {
    let error = false;
    //check time
    if (moment(groundTurnBackTime.timeLeft).toDate() >= moment(groundTurnBackTime.timeReturn).toDate()) {
      setTimeError(timeErrorString);
      error = true;
    }
    if (groundTurnBackComment === null || groundTurnBackComment === "") {
      setTextError("Must include a comment");
      error = true;
    }
    if (groundTurnBackReason === null) {
      setReasonError("Must select reason");
      error = true;
    }
    return error;
  };

  const handleConfirm = (): void => {
    if (checkForErrors() === true) {
      return;
    }
    setIsMakingAPICall(true);
    //do the logic etc...
    // calcualate how much of an increament to add to each of the actual flight times
    const durationToShiftFlightTimes =
      groundTurnBackTime.timeReturn + increment - selectedFlights[0].actualOut?.valueOf();

    const actualOff = selectedFlights[0]?.actualOff?.valueOf() + durationToShiftFlightTimes;
    const actualOn = selectedFlights[0]?.actualOn?.valueOf() + durationToShiftFlightTimes;
    const actualOut = selectedFlights[0]?.actualOut?.valueOf() + durationToShiftFlightTimes;
    const actualIn = selectedFlights[0]?.actualIn?.valueOf() + durationToShiftFlightTimes;

    const data: ICreateGTBData = {
      newActualDepatureTime: {
        actualOff: actualOff,
        actualOn: actualOn,
        actualOut: actualOut,
        actualIn: actualIn,
        "flightType.isGroundTurnBack": true,
      },
      groundEventData: {
        actualStart: groundTurnBackTime.timeLeft,
        actualEnd: groundTurnBackTime.timeReturn,
        diversionReason: groundTurnBackReason.label,
        comments: groundTurnBackComment,
        eventType: eventType[0],
        aircraft: selectedAircraft[0],
      },
    };

    selectedFlights.forEach(async (selectedFlight: IFlight) => {
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Setting Ground Turn Back...",
        successMessage: "Ground Turn Back Successfully Created",
        errorMessage: "Error: Unable to Create Ground Turn Back",
      };

      const result = await AmplifyAPIWrapper.Put<ICreateGTBData>(
        `/groundEvents/createGroundTurnBackEvent/${selectedFlight._id}`,
        data,
        notifications
      );
      setIsMakingAPICall(false);
      if (result?.hasOwnProperty("newEvent")) {
        //wait to close modal until success...
        handleClose(false);
        setActiveEventModal(EventModalTypes.None);
      } else {
        handleClose(false);
        setActiveEventModal(EventModalTypes.None);
      }
    });
  };

  if (isOpen && arrayIsNotNull()) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          <FlightChipCollection flights={selectedFlights} />
          <ModalTitle title="Ground Turn Back"></ModalTitle>

          <DateTimeField
            className={"datetimeInput"}
            name="groundTurnBack_outtime"
            label={timeInputLabel[0]}
            showAirportTime={airportIdent}
            value={groundTurnBackTime?.timeLeft}
            onChange={handleMinDateChange}
            maxDate={groundTurnBackTime?.timeReturn}
            isEpoch={true}
          />

          <DateTimeField
            className={"datetimeInput"}
            name="groundTurnBack_returntime"
            label={timeInputLabel[1]}
            showAirportTime={airportIdent}
            value={groundTurnBackTime.timeReturn}
            onChange={handleMaxDateChange}
            minDate={groundTurnBackTime.timeLeft}
            isEpoch={true}
          />
          {!!timeError && <ModalErrorText error={timeError}></ModalErrorText>}

          <SelectField
            className="modal_selection"
            label="Reason"
            name="groundTurnBackReason"
            value={groundTurnBackReason}
            onChange={nativeFormHandler}
            options={diversionTitles}
            // getOptionLabel={(AC: IACOption) => `${AC.registration} (${AC.title})`}
            // getOptionValue={(AC: IACOption) => AC.registration}
            // noOptionsMessage={() => "Search for airport name or IATA code"}
            isSearchable={true}
            isClearable={true}
            menuPosition="fixed"
            maxMenuHeight={400}
            openMenuOnFocus={false}
            placeholder="Select Reason..."
          />

          {!!reasonError && <ModalErrorText error={reasonError}></ModalErrorText>}

          <TextField
            className={"modalTextfield"}
            label="Comments"
            name="cancelEventComments"
            value={groundTurnBackComment}
            onChange={textFieldHandler}
            placeholder="Comments..."
            multiline={true}
            minRows={2}
            maxRows={4}
          />

          {!!textError && <ModalErrorText error={textError}></ModalErrorText>}

          <DialogActions>
            <ModalButton label="Cancel" handler={handleCancel} disable={isMakingAPICall}></ModalButton>
            <ModalButton label="Confirm" handler={debounce(handleConfirm, 500)} disable={isMakingAPICall}></ModalButton>
          </DialogActions>
        </div>
      </DialogContent>
    );
  } else {
    return null;
  }
};
